import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GrainGradient } from '@paper-design/shaders-react';
import { useFeaturedProjects } from '../hooks/useProjects';
import { useReducedMotion } from '../hooks/useReducedMotion';
import HomeParallax from '../components/sections/HomeParallax';
import IllustrationModal from '../components/modals/IllustrationModal';
import GlobalHeader from '../components/layout/GlobalHeader';

// 셰이더 기본 오프셋 — 마우스 위치에 따라 이 값 주변으로 살짝 움직임
const BASE_OFFSET = { x: -0.12, y: -0.22 };
const MOUSE_INFLUENCE = 0.25; // 마우스 추적 강도 (0=고정, 1=화면 끝까지 크게 이동)

export default function HomePage() {
  const [modalProject, setModalProject] = useState(null);
  const { projects: featured, loading } = useFeaturedProjects(4);
  const reducedMotion = useReducedMotion();
  const [shaderOffset, setShaderOffset] = useState(BASE_OFFSET);

  // 마우스를 따라 셰이더가 살짝 움직이도록 — rAF로 한 프레임에 한 번만 갱신(성능)
  useEffect(() => {
    if (reducedMotion) return;
    let frame = null;
    let latestEvent = null;

    const handleMouseMove = (e) => {
      latestEvent = e;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        const nx = (latestEvent.clientX / window.innerWidth - 0.5) * 2;  // -1..1
        const ny = (latestEvent.clientY / window.innerHeight - 0.5) * 2; // -1..1
        setShaderOffset({
          x: BASE_OFFSET.x + nx * MOUSE_INFLUENCE,
          y: BASE_OFFSET.y + ny * MOUSE_INFLUENCE,
        });
        frame = null;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [reducedMotion]);

  return (
    <>
      {/* 배경 셰이더 투명도 — 0(완전 투명) ~ 1(완전 불투명) 사이 값으로 직접 조절하세요 */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, opacity: 0.2 }}>
        {reducedMotion ? (
          // 모션 최소화 설정 사용자 — WebGL 캔버스를 아예 마운트하지 않아 GPU 비용 없이
          // 같은 톤의 정적 그라디언트로 대체 (접근성 + 성능)
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #ebfaff, #e1f6fe, #bbe0ff, #f0f7ff)',
            }}
          />
        ) : (
          <GrainGradient
            width="100%"
            height="100%"
            colors={["#0095ff24", "#ebfaff", "#d0f2ff", "#c7e6ff"]}
            colorBack="#f0f7ff"
            softness={0.38}
            intensity={1}
            noise={0.69}
            shape="corners"
            speed={1.5}
            scale={0.84}
            offsetX={shaderOffset.x}
            offsetY={shaderOffset.y}
          />
        )}
      </div>

      <GlobalHeader hideThreshold={80} hamburgerOnly />

      <main style={{ height: '100vh', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a3a3a3', fontSize: '0.875rem' }}>
            Loading...
          </div>
        ) : (
          <HomeParallax projects={featured} onViewProject={setModalProject} />
        )}
      </main>

      <Link to="/contact" className="float-contact" aria-label="Contact">
        Contact
      </Link>

      {modalProject && (
        <IllustrationModal
          project={modalProject}
          onClose={() => setModalProject(null)}
        />
      )}
    </>
  );
}
